import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { compressImage, uploadInChunks } from "../lib/upload";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart, Send, Trash2, Star, CornerDownRight, MessageCircle, ImagePlus, X, Camera, Clock, Headphones, Users, Loader2 } from "lucide-react";
import VoicePlaylist from "./VoicePlaylist";
import { toast } from "sonner";
import api, { getErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { markSeen } from "../lib/cheerSeen";
import { useProgress } from "../context/ProgressContext";
import { SENT_EVENT, PROGRESS_EVENT, dropQueuedCheer, dropQueuedReply, queueCheer, queuedFor, queuedRepliesFor } from "../lib/cheerQueue";
import { putPhoto, putVoice, getPhoto, deleteMedia, countPhotos, voiceKey, MAX_QUEUED_MEDIA } from "../lib/photoStore";
import VoiceRecorder from "./VoiceRecorder";
import VoicePlayer from "./VoicePlayer";

const SUGGESTIONS = ["Well done!", "So proud of you!", "Keep that streak going!", "You're a money star!"];

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Encouragement notes for a child. compact=true renders the read-only strip used on the lesson page.
const Cheers = ({ child, compact = false, canPost = true, limit = 20 }) => {
  const { user } = useAuth();
  const { syncStatus } = useProgress();
  const online = syncStatus.online;
  const [cheers, setCheers] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyVoice, setReplyVoice] = useState(null); // { blob, url, seconds, mime }
  const [photo, setPhoto] = useState(null); // { file, preview }
  const [voice, setVoice] = useState(null); // { blob, url, seconds, mime }
  const [progress, setProgress] = useState(null);
  const [lightbox, setLightbox] = useState(null); // cheer id
  const [playId, setPlayId] = useState(null); // cheer to auto-play (from the voice alert email)
  const [playlist, setPlaylist] = useState(false);
  const fileRef = useRef(null);
  const isServerChild = !!child?.ownerId || !!child?.readOnly;
  const token = localStorage.getItem("tycc_token");
  const location = useLocation();
  const navigate = useNavigate();
  // One-tap cheer from the certificate email: /family?cheer=<text>#child-<id>
  useEffect(() => {
    if (compact || !child?.id) return;
    const prefill = new URLSearchParams(location.search).get("cheer");
    if (!prefill || location.hash !== `#child-${child.id}`) return;
    setText(prefill);
    setTimeout(() => {
      const input = document.getElementById(`cheer-input-${child.id}`);
      input?.focus({ preventScroll: true });
      // Card is taller than a phone screen: bring the "Cheers for <name>" panel into view so the child is still identified
      const panel = input?.closest("[data-testid='cheers-panel']");
      if (panel) window.scrollTo({ top: panel.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
    }, 600);
    navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
  }, [location.search, location.hash, child?.id, compact, navigate, location.pathname]);
  const photoUrl = (id, size) => `${process.env.REACT_APP_BACKEND_URL}/api/cheer-photos/${id}?size=${size}&auth=${token}`;

  const asLocal = (q) => ({ id: q.localId, text: q.text, author_id: q.authorId, author_name: q.authorName, created_at: q.createdAt, stars: 0, starred: false, queued: true, hasPhoto: !!q.hasPhoto, hasVoice: !!q.hasVoice });
  const withQueuedReplies = async (list, childId) => {
    const byCheer = Object.fromEntries(queuedRepliesFor(childId).map((q) => [q.cheerId, q]));
    return Promise.all(list.map(async (c) => {
      const q = byCheer[c.id];
      if (!q) return c;
      const stored = q.hasVoice ? await getPhoto(voiceKey(q.localId)).catch(() => null) : null;
      return { ...c, reply: { text: q.text, author_name: q.authorName, queued: true, localId: q.localId, localVoiceUrl: stored?.blob ? URL.createObjectURL(stored.blob) : null } };
    }));
  };
  // Offline media lives in IndexedDB: attach object URLs for the local preview / playback
  const hydratePhotos = async (items) => Promise.all(items.map(async (c) => {
    const out = { ...c };
    if (c.hasPhoto) { const s = await getPhoto(c.id).catch(() => null); if (s?.blob) out.localPhotoUrl = URL.createObjectURL(s.blob); }
    if (c.hasVoice) { const s = await getPhoto(voiceKey(c.id)).catch(() => null); if (s?.blob) out.localVoiceUrl = URL.createObjectURL(s.blob); }
    return out;
  }));

  useEffect(() => {
    if (!user || !child?.id || !isServerChild) { setCheers([]); return; }
    let alive = true;
    (async () => {
      const queued = await hydratePhotos(queuedFor(child.id).map(asLocal).reverse());
      const cacheKey = `tycc_cheers_cache_${child.id}`;
      try {
        const r = await api.get(`/children/${child.id}/cheers`);
        try { localStorage.setItem(cacheKey, JSON.stringify(r.data.slice(0, 30))); } catch { /* storage full */ }
        const list = await withQueuedReplies(r.data, child.id);
        if (alive) { setCheers([...queued, ...list]); markSeen(child.id, r.data); }
      } catch {
        // Offline: fall back to the last list we saw so queued thank-yous still have a cheer to sit under
        let cached = []; try { cached = JSON.parse(localStorage.getItem(cacheKey) || "[]"); } catch { cached = []; }
        const list = await withQueuedReplies(cached, child.id);
        if (alive) setCheers([...queued, ...list]);
      }
    })();
    return () => { alive = false; };
  }, [user, child?.id, isServerChild]);

  // Upload progress for queued media ("Sending 42%")
  useEffect(() => {
    const onProgress = (e) => setCheers((c) => (c ? c.map((x) => (x.id === e.detail.localId ? { ...x, sending: e.detail.percent } : x.reply?.localId === e.detail.localId ? { ...x, reply: { ...x.reply, sending: e.detail.percent } } : x)) : c));
    window.addEventListener(PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(PROGRESS_EVENT, onProgress);
  }, []);

  // A queued cheer / thank-you reached the server: swap the local copy for the real one
  useEffect(() => {
    const onSent = (e) => setCheers((c) => {
      if (!c) return c;
      const { kind, localId, cheerId, cheer } = e.detail;
      if (kind === "reply") return c.map((x) => (x.id === cheerId ? { ...x, reply: cheer.reply } : x));
      return c.map((x) => (x.id === localId ? cheer : x));
    });
    window.addEventListener(SENT_EVENT, onSent);
    return () => window.removeEventListener(SENT_EVENT, onSent);
  }, []);

  // One-tap play from the voice alert email: /family?play=<cheerId>#child-<id>
  useEffect(() => {
    if (compact || !cheers || !child?.id) return;
    const id = new URLSearchParams(location.search).get("play");
    if (!id || !cheers.some((c) => c.id === id && c.voice)) return;
    setPlayId(id);
    setTimeout(() => document.querySelector(`[data-testid="cheer-item-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
  }, [cheers, compact, child?.id, location.search, location.pathname, location.hash, navigate]);

  const daysWaiting = (c) => Math.floor((Date.now() - new Date(c.created_at).getTime()) / 86400000);
  const waiting = !compact && !child?.readOnly ? (cheers || []).filter((c) => c.from_follower && !c.reply && daysWaiting(c) >= 7) : [];

  if (!user || !isServerChild) return null;

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo is larger than 8 MB"); return; }
    if (!/^image\//.test(f.type) && !/\.hei[cf]$/i.test(f.name)) { toast.error("Please choose a photo (JPG, PNG, WebP or HEIC)"); return; }
    setPhoto({ file: f, preview: /^image\/(jpeg|png|webp|gif)$/.test(f.type) ? URL.createObjectURL(f) : null });
  };

  const send = async (msg) => {
    const body = (msg ?? text).trim() || (photo ? `Sent ${child.panda.name} a photo` : voice ? `Sent ${child.panda.name} a voice note` : "");
    if (!body) return;
    const queueIt = async () => {
      const localId = `q-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      let localPhotoUrl = null, localVoiceUrl = null;
      const need = (photo ? 1 : 0) + (voice ? 1 : 0);
      if (need && (await countPhotos().catch(() => 0)) + need > MAX_QUEUED_MEDIA) { toast.error(`Up to ${MAX_QUEUED_MEDIA} photos or voice notes can wait offline`, { description: "Connect once so the earlier ones can send, then try again." }); return; }
      try {
        if (photo) { const file = await compressImage(photo.file); await putPhoto(localId, file); localPhotoUrl = URL.createObjectURL(file); }
        if (voice) { await putVoice(localId, voice.blob, voice.mime); localVoiceUrl = URL.createObjectURL(voice.blob); }
      } catch { deleteMedia(localId).catch(() => {}); toast.error("Could not store the attachment on this device", { description: "Your note was not sent. Try again when you're online." }); return; }
      const q = queueCheer({ localId, childId: child.id, childName: child.panda.name, text: body, authorId: user.id, authorName: user.name, createdAt: new Date().toISOString(), hasPhoto: !!photo, hasVoice: !!voice });
      setCheers((c) => [{ ...asLocal(q), localPhotoUrl, localVoiceUrl }, ...(c || [])]);
      setText("");
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
      setPhoto(null);
      setVoice(null);
      toast(`You're offline - your cheer for ${child.panda.name} will send when you're back online.`);
    };
    if (!online) { await queueIt(); return; }
    setBusy(true);
    try {
      const r = await api.post(`/children/${child.id}/cheers`, { text: body });
      let cheer = r.data;
      if (photo) {
        try {
          setProgress(0);
          const file = await compressImage(photo.file);
          const uploadId = await uploadInChunks(file, setProgress);
          const p = await api.post(`/children/${child.id}/cheers/${cheer.id}/photo`, { upload_id: uploadId });
          cheer = p.data;
        } catch (err) {
          toast.error(getErrorMessage(err, "Photo could not be attached"), { description: "Your note was sent without the photo." });
        } finally { setProgress(null); }
      }
      if (voice) {
        try {
          setProgress(0);
          const v = await api.post(`/children/${child.id}/cheers/${cheer.id}/voice`, { upload_id: await uploadVoice(voice) });
          cheer = v.data;
        } catch (err) {
          toast.error(getErrorMessage(err, "Voice note could not be attached"), { description: "Your note was sent without the recording." });
        } finally { setProgress(null); }
      }
      setCheers((c) => { const next = [cheer, ...(c || [])]; markSeen(child.id, next); return next; });
      setText("");
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
      if (voice?.url) URL.revokeObjectURL(voice.url);
      setPhoto(null);
      setVoice(null);
      toast.success(`Cheer sent to ${child.panda.name}!`);
    } catch (err) {
      if (!err?.response) { await queueIt(); return; } // network dropped mid-send
      toast.error(getErrorMessage(err, "Could not send cheer"));
    } finally { setBusy(false); }
  };

  const PhotoThumb = ({ c, size = 72 }) => (c.photo || c.localPhotoUrl) ? (
    <button type="button" onClick={() => setLightbox(c.id)} className="mt-2 relative block rounded-xl overflow-hidden border-2 border-white shadow hover:scale-[1.03] transition-transform" style={{ width: size, height: size }} aria-label="View photo" data-testid={`cheer-photo-thumb-${c.id}`} data-local={c.localPhotoUrl ? "true" : "false"}>
      <img src={c.localPhotoUrl || photoUrl(c.id, "thumb")} alt={`Photo from ${c.author_name}`} className="w-full h-full object-cover" loading="lazy" />
      <SendingRing c={c} />
    </button>
  ) : null;

  const Lightbox = () => {
    const c = (cheers || []).find((x) => x.id === lightbox);
    return (
      <Dialog open={!!c} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white rounded-3xl" data-testid="cheer-photo-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {c && (
            <>
              <img src={c.localPhotoUrl || photoUrl(c.id, "full")} alt={`Photo from ${c.author_name}`} className="w-full max-h-[75vh] object-contain rounded-2xl" />
              <p className="px-3 pb-2 text-sm text-[#0c4a6e]"><span className="font-bold">{c.author_name}:</span> {c.text}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const remove = async (id) => {
    if (String(id).startsWith("q-")) { dropQueuedCheer(id); deleteMedia(id).catch(() => {}); setCheers((c) => { const cur = c.find((x) => x.id === id); if (cur?.localPhotoUrl) URL.revokeObjectURL(cur.localPhotoUrl); if (cur?.localVoiceUrl) URL.revokeObjectURL(cur.localVoiceUrl); return c.filter((x) => x.id !== id); }); return; }
    try {
      await api.delete(`/children/${child.id}/cheers/${id}`);
      setCheers((c) => c.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not remove"));
    }
  };

  const star = async (id) => {
    try {
      const r = await api.post(`/children/${child.id}/cheers/${id}/star`);
      setCheers((c) => c.map((x) => (x.id === id ? { ...x, stars: r.data.stars, starred: r.data.starred } : x)));
      if (r.data.starred) toast.success(child.readOnly ? "Starred!" : "Thanks sent!", { description: child.readOnly ? `You starred this cheer for ${child.panda.name}.` : `${child.panda.name} starred this cheer.` });
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not star"));
    }
  };

  const StarButton = ({ c, size = 14 }) => (
    <button
      type="button"
      onClick={() => star(c.id)}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold transition-colors ${c.starred ? "bg-[#fff8e1] text-[#8a6d00]" : "text-gray-400 hover:bg-[#fff8e1] hover:text-[#8a6d00]"}`}
      aria-label={c.starred ? "Remove star" : "Say thanks with a star"}
      title={c.starred ? "You starred this" : "Say thanks with a star"}
      data-testid={`cheer-star-${c.id}`}
    >
      <Star size={size} fill={c.starred ? "#f3c932" : "none"} className={c.starred ? "text-[#f3c932] heart-pop" : ""} />
      <span data-testid={`cheer-star-count-${c.id}`}>{c.stars || 0}</span>
    </button>
  );

  const canReply = !child?.readOnly; // owner / co-parent (on the child's behalf)

  const uploadVoice = async (v) => {
    const ext = /mp4/.test(v.mime) ? "m4a" : /ogg/.test(v.mime) ? "ogg" : "webm";
    return uploadInChunks(new File([v.blob], `voice-note.${ext}`, { type: v.mime }), setProgress);
  };

  const saveReply = async (id) => {
    const body = replyText.trim();
    if (!body && !replyVoice) return;
    const queueReply = async () => {
      const prev = queuedRepliesFor(child.id).find((q) => q.cheerId === id);
      if (prev) { dropQueuedReply(child.id, id); deleteMedia(prev.localId).catch(() => {}); }
      const localId = `r-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      let localVoiceUrl = null;
      if (replyVoice) {
        if ((await countPhotos().catch(() => 0)) >= MAX_QUEUED_MEDIA) { toast.error(`Up to ${MAX_QUEUED_MEDIA} photos or voice notes can wait offline`, { description: "Connect once so the earlier ones can send, then try again." }); return; }
        try { await putVoice(localId, replyVoice.blob, replyVoice.mime); localVoiceUrl = URL.createObjectURL(replyVoice.blob); } catch { toast.error("Could not store the recording on this device"); return; }
      }
      queueCheer({ kind: "reply", localId, childId: child.id, childName: child.panda.name, cheerId: id, text: body, authorName: child.panda.name, createdAt: new Date().toISOString(), hasVoice: !!replyVoice });
      setCheers((c) => c.map((x) => (x.id === id ? { ...x, reply: { text: body, author_name: child.panda.name, queued: true, localId, localVoiceUrl } } : x)));
      setReplyingTo(null); setReplyText(""); setReplyVoice(null);
      toast(`You're offline - ${child.panda.name}'s thank-you will send when you're back online.`);
    };
    if (!online) { await queueReply(); return; }
    setBusy(true);
    try {
      let updated = null;
      if (body) updated = (await api.put(`/children/${child.id}/cheers/${id}/reply`, { text: body })).data;
      if (replyVoice) {
        try {
          setProgress(0);
          updated = (await api.post(`/children/${child.id}/cheers/${id}/reply/voice`, { upload_id: await uploadVoice(replyVoice) })).data;
        } catch (err) {
          toast.error(getErrorMessage(err, "Voice thank-you could not be attached"), { description: body ? "The written thank-you was saved." : "Please try recording again." });
          if (!body) return;
        } finally { setProgress(null); }
      }
      setCheers((c) => c.map((x) => (x.id === id ? { ...x, reply: updated.reply } : x)));
      if (replyVoice?.url) URL.revokeObjectURL(replyVoice.url);
      setReplyingTo(null); setReplyText(""); setReplyVoice(null);
      toast.success("Thank-you sent", { description: `${child.panda.name}'s ${replyVoice ? "voice " : ""}reply now shows under the cheer.` });
    } catch (err) {
      if (!err?.response) { await queueReply(); return; }
      toast.error(getErrorMessage(err, "Could not save reply"));
    } finally { setBusy(false); }
  };

  const removeReply = async (id) => {
    const cur = (cheers || []).find((x) => x.id === id);
    if (cur?.reply?.queued) { dropQueuedReply(child.id, id); if (cur.reply.localId) deleteMedia(cur.reply.localId).catch(() => {}); setCheers((c) => c.map((x) => (x.id === id ? { ...x, reply: null } : x))); return; }
    try {
      await api.delete(`/children/${child.id}/cheers/${id}/reply`);
      setCheers((c) => c.map((x) => (x.id === id ? { ...x, reply: null } : x)));
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not remove reply"));
    }
  };

  const ReplyLine = ({ c }) => c.reply ? (
    <div data-testid={`cheer-reply-${c.id}`}>
      {c.reply.text && (
        <p className="mt-1.5 text-xs text-[#0d4d33] bg-[#e8f8ef] rounded-xl px-3 py-1.5 inline-flex items-start gap-1.5 max-w-full" data-testid={`cheer-reply-text-${c.id}`}>
          <CornerDownRight size={12} className="mt-0.5 flex-shrink-0" />
          <span><span className="font-bold">{c.reply.author_name}:</span> {c.reply.text}</span>
        </p>
      )}
      {c.reply.queued ? (
        <>
          {c.reply.localVoiceUrl && <LocalVoice url={c.reply.localVoiceUrl} testId={`reply-local-voice-${c.id}`} tone="green" />}
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#fff3d6] text-[#8a5a00] text-[11px] font-bold px-2 py-0.5 align-middle" data-testid={`reply-queued-${c.id}`} data-sending={c.reply.sending ?? undefined}>{c.reply.sending != null ? <><Loader2 size={11} className="animate-spin" /> Sending {c.reply.sending}%</> : <><Clock size={11} /> Will send when online</>}</span>
        </>
      ) : (
        <VoicePlayer cheer={c} author={c.reply.author_name} part="reply" tone="green" />
      )}
    </div>
  ) : null;

  const QueuedBadge = ({ c }) => c.queued ? (
    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#fff3d6] text-[#8a5a00] text-[11px] font-bold px-2 py-0.5" data-testid={`cheer-queued-${c.id}`} data-sending={c.sending ?? undefined}>{c.sending != null ? <><Loader2 size={11} className="animate-spin" /> Sending {c.sending}%</> : <><Clock size={11} /> Will send when online</>}</span>
  ) : null;

  // Plays a clip that is still on this device (offline queue); the real VoicePlayer streams from the server
  const LocalVoice = ({ url, testId, tone = "pink" }) => (
    <audio src={url} controls preload="metadata" className={`mt-2 h-8 w-full max-w-[240px] rounded-full ${tone === "green" ? "accent-[#63c79a]" : "accent-[#e5356f]"}`} data-testid={testId} />
  );
  const SendingRing = ({ c }) => c.sending != null ? (
    <span className="absolute inset-0 bg-black/45 text-white text-[11px] font-bold flex items-center justify-center rounded-xl" data-testid={`cheer-sending-${c.id}`}>{c.sending}%</span>
  ) : null;

  const list = (cheers || []).slice(0, limit);

  if (compact) {
    if (!list.length) return null;
    const voiced = (cheers || []).filter((c) => c.voice);
    const monthKey = new Date().toISOString().slice(0, 7);
    const fans = new Set((cheers || []).filter((c) => c.created_at.slice(0, 7) === monthKey).map((c) => c.author_id)).size;
    return (
      <div className="bg-[#fff0f5] border-2 border-[#f9a8c9] rounded-3xl p-5" data-testid="cheers-strip">
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-bold text-[#7a1f45] inline-flex items-center gap-2"><Heart size={18} className="text-[#e5356f]" fill="#e5356f" /> Cheers for {child.panda.name} <span className="text-xs font-normal text-[#7a1f45]/70">- tap a star to say thanks</span></p>
          {fans > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-white border border-[#f9a8c9] text-[#7a1f45] text-xs font-bold px-2.5 py-1" title="Different people who cheered this month" data-testid="fans-badge"><Users size={12} className="text-[#e5356f]" /> <span data-testid="fans-count">{fans}</span> fan{fans === 1 ? "" : "s"} this month</span>}
          {voiced.length > 0 && <button type="button" onClick={() => setPlaylist(true)} className="ml-auto btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white text-sm inline-flex items-center gap-2 shadow" data-testid="voice-playlist-button"><Headphones size={16} /> Listen to your fans ({voiced.length})</button>}
        </div>
        {playlist && <VoicePlaylist cheers={cheers} childName={child.panda.name} onClose={() => setPlaylist(false)} />}
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
          {list.map((c) => (
            <div key={c.id} className="min-w-[220px] max-w-[280px] bg-white rounded-2xl px-4 py-3 shadow-sm bubble-in" data-testid={`cheer-item-${c.id}`}>
              <p className="text-[#0c4a6e] font-semibold leading-snug">“{c.text}”</p>
              <PhotoThumb c={c} size={64} />
              <VoicePlayer cheer={c} author={c.author_name} />
              {c.localVoiceUrl && <LocalVoice url={c.localVoiceUrl} testId={`cheer-local-voice-${c.id}`} />}
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">- {c.author_name} · {timeAgo(c.created_at)}</p>
                {c.queued ? <QueuedBadge c={c} /> : <StarButton c={c} />}
              </div>
              <ReplyLine c={c} />
            </div>
          ))}
        </div>
        <Lightbox />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#f9a8c9] bg-[#fff0f5]/60 p-4" data-testid="cheers-panel">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-[#7a1f45] text-sm inline-flex items-center gap-2"><Heart size={16} className="text-[#e5356f]" fill="#e5356f" /> Cheers {cheers ? `(${cheers.length})` : ""}</p>
        {waiting.length > 0 && (
          <button type="button" onClick={() => { setReplyingTo(waiting[0].id); setReplyText(""); setReplyVoice(null); document.querySelector(`[data-testid="cheer-item-${waiting[0].id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }} className="inline-flex items-center gap-1 rounded-full bg-[#fff8e1] border border-[#f3c932] text-[#8a6d00] text-xs font-bold px-2.5 py-0.5 hover:bg-[#f3c932] hover:text-[#5a4600] transition-colors" title="Grandparent cheers over a week old without a thank-you" data-testid={`thanks-waiting-${child.id}`}>
            <Clock size={12} /> {waiting.length} waiting for a thank-you
          </button>
        )}
      </div>
      {canPost && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            <input id={`cheer-input-${child.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={140} placeholder={`Send ${child.panda.name} a note...`} className="flex-1 min-w-[160px] h-10 rounded-full px-4 border-2 border-[#f9a8c9] bg-white focus:outline-none focus:border-[#e5356f] text-sm" data-testid="cheer-input" onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
            <input ref={fileRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={pickPhoto} data-testid="cheer-photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors disabled:opacity-50 ${photo ? "bg-[#e5356f] border-[#e5356f] text-white" : "border-[#f9a8c9] text-[#e5356f] hover:bg-[#ffe0ec]"}`} title={online ? "Attach a photo" : "Attach a photo - it uploads when you're back online"} aria-label="Attach a photo" data-testid="cheer-photo-button"><ImagePlus size={16} /></button>
            {!voice && <VoiceRecorder value={null} onChange={setVoice} disabled={busy} />}
            <button onClick={() => send()} disabled={busy || (!text.trim() && !photo && !voice)} className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white text-sm px-4 py-1.5 inline-flex items-center gap-1 disabled:opacity-50" data-testid="cheer-send"><Send size={14} /> {progress !== null ? `${progress}%` : "Send"}</button>
          </div>
          {voice && <div className="mt-2"><VoiceRecorder value={voice} onChange={setVoice} disabled={busy} /></div>}
          {!online && <p className="mt-2 text-[11px] font-semibold text-[#8a5a00] inline-flex items-center gap-1" data-testid="cheer-offline-note"><Clock size={11} /> You're offline - cheers, photos and voice notes are saved on this device and send when you're back online.</p>}
          {photo && (
            <div className="mt-2 inline-flex items-center gap-2 bg-white rounded-2xl p-1.5 pr-3 border border-[#f9a8c9]" data-testid="cheer-photo-pending">
              {photo.preview ? <img src={photo.preview} alt="Selected" className="w-12 h-12 rounded-xl object-cover" /> : <span className="w-12 h-12 rounded-xl bg-[#fff0f5] text-[#e5356f] flex items-center justify-center"><Camera size={18} /></span>}
              <span className="text-xs text-[#7a1f45] font-semibold truncate max-w-[160px]">{photo.file.name}</span>
              <button type="button" onClick={() => { if (photo.preview) URL.revokeObjectURL(photo.preview); setPhoto(null); }} className="text-gray-400 hover:text-[#e5356f]" aria-label="Remove photo" data-testid="cheer-photo-remove"><X size={14} /></button>
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} disabled={busy} className="text-xs font-bold text-[#7a1f45] bg-white border border-[#f9a8c9] rounded-full px-3 py-1 hover:bg-[#e5356f] hover:text-white transition-colors" data-testid="cheer-suggestion">{s}</button>)}
          </div>
        </div>
      )}
      {list.length > 0 && (
        <ul className="mt-3 space-y-2">
          {list.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-sm bg-white rounded-xl px-3 py-2" data-testid={`cheer-item-${c.id}`}>
              <span className="w-7 h-7 rounded-full bg-[#7d5fb2] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{c.author_name.charAt(0).toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[#0c4a6e]">{c.text}</p>
                <p className="text-xs text-gray-500">{c.author_name} · {timeAgo(c.created_at)}</p>
                <QueuedBadge c={c} />
                <PhotoThumb c={c} />
                <VoicePlayer cheer={c} author={c.author_name} autoPlay={playId === c.id} />
                {c.localVoiceUrl && <LocalVoice url={c.localVoiceUrl} testId={`cheer-local-voice-${c.id}`} />}
                <ReplyLine c={c} />
                {c.queued ? null : canReply && replyingTo === c.id ? (
                  <div className="mt-2" data-testid={`reply-form-${c.id}`}>
                    <div className="flex gap-2 items-center">
                      <input autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} maxLength={100} placeholder={`${child.panda.name} says...`} className="flex-1 h-8 rounded-full px-3 border-2 border-[#63c79a] bg-white text-xs focus:outline-none" data-testid={`reply-input-${c.id}`} onKeyDown={(e) => { if (e.key === "Enter") saveReply(c.id); if (e.key === "Escape") { setReplyingTo(null); setReplyVoice(null); } }} />
                      {!replyVoice && <VoiceRecorder value={null} onChange={setReplyVoice} disabled={busy} />}
                      <button onClick={() => saveReply(c.id)} disabled={busy || (!replyText.trim() && !replyVoice)} className="text-xs font-bold text-white bg-[#63c79a] hover:bg-[#4fb98a] rounded-full px-3 h-8 disabled:opacity-50" data-testid={`reply-save-${c.id}`}>{progress !== null ? `${progress}%` : "Send"}</button>
                      <button onClick={() => { setReplyingTo(null); setReplyVoice(null); }} className="text-xs font-bold text-gray-400 hover:text-gray-600" data-testid={`reply-cancel-${c.id}`}>Cancel</button>
                    </div>
                    {replyVoice && <div className="mt-2"><VoiceRecorder value={replyVoice} onChange={setReplyVoice} disabled={busy} /></div>}
                  </div>
                ) : canReply && (
                  <div className="mt-1 flex gap-3">
                    <button onClick={() => { setReplyingTo(c.id); setReplyText(c.reply?.text || ""); setReplyVoice(null); }} className="text-xs font-bold text-[#2e8b57] hover:underline inline-flex items-center gap-1" data-testid={`reply-button-${c.id}`}><MessageCircle size={12} /> {c.reply ? "Edit thank-you" : "Say thanks"}</button>
                    {c.from_follower && !c.reply && daysWaiting(c) >= 7 && <span className="text-[11px] text-[#8a6d00] inline-flex items-center gap-1" data-testid={`thanks-waiting-item-${c.id}`}><Clock size={11} /> waiting {daysWaiting(c)} days</span>}
                    {c.reply && <button onClick={() => removeReply(c.id)} className="text-xs font-bold text-gray-400 hover:text-[#e5356f]" data-testid={`reply-delete-${c.id}`}>Remove</button>}
                  </div>
                )}
              </div>
              {!c.queued && <StarButton c={c} />}
              {(c.author_id === user.id || !child.readOnly) && <button onClick={() => remove(c.id)} className="text-gray-300 hover:text-[#e5356f]" aria-label="Remove cheer"><Trash2 size={14} /></button>}
            </li>
          ))}
        </ul>
      )}
      {cheers && cheers.length === 0 && <p className="mt-2 text-xs text-gray-500 italic">No cheers yet - be the first!</p>}
      <Lightbox />
    </div>
  );
};

export default Cheers;
